/**
 * Numeric precision contract — built from domain.json + orch defaults.
 * See bridge plan §B.12 (Field → Form → Engine).
 */

import {
  ENGINE_DEFAULTS,
  resolveInputPrecision,
  roundToDecimalPlaces,
} from '@packages/core';
import type { RawDomainSchemaProperty } from '../resolvers/types';

export interface NumericFieldPrecisionEntry {
  inputPrecision: number;
  type: 'number' | 'integer';
}

export interface FormPrecisionConfig {
  formDefaults?: {
    inputPrecision?: number;
    displayPrecision?: number;
  };
  /** Domain-relative paths, e.g. `grantValue`, `settlementConfiguration.amount`. */
  fields: Record<string, NumericFieldPrecisionEntry>;
}

export interface FormDefaultsConfig {
  inputPrecision?: number;
  displayPrecision?: number;
}

function collectNumericFields(
  properties: Record<string, RawDomainSchemaProperty>,
  formDefaults: FormDefaultsConfig | undefined,
  prefix = '',
  out: Record<string, NumericFieldPrecisionEntry> = {},
): Record<string, NumericFieldPrecisionEntry> {
  for (const [key, prop] of Object.entries(properties)) {
    const path = prefix ? `${prefix}.${key}` : key;
    const fieldType = prop.type;

    if (fieldType === 'number' || fieldType === 'integer') {
      const inputPrecision = resolveInputPrecision({
        fieldInputPrecision: prop.inputPrecision,
        formInputPrecision: formDefaults?.inputPrecision,
        fieldType,
      });
      out[path] = { inputPrecision, type: fieldType };
    }

    const nested = prop.properties as Record<string, RawDomainSchemaProperty> | undefined;
    if (prop.type === 'object' && nested) {
      collectNumericFields(nested, formDefaults, path, out);
    }
  }
  return out;
}

export function buildFormPrecisionConfig(
  properties: Record<string, RawDomainSchemaProperty>,
  orchDefaults?: FormDefaultsConfig,
): FormPrecisionConfig {
  return {
    ...(orchDefaults != null && Object.keys(orchDefaults).length > 0 && { formDefaults: orchDefaults }),
    fields: collectNumericFields(properties, orchDefaults),
  };
}

/** Strip repeatable row prefix (`items.N.`) to domain-relative path. */
export function toDomainRelativeFormPath(formPath: string): string {
  const parts = formPath.split('.');
  const itemsIdx = parts.indexOf('items');
  if (itemsIdx >= 0 && parts.length > itemsIdx + 2 && /^\d+$/.test(parts[itemsIdx + 1] ?? '')) {
    return parts.slice(itemsIdx + 2).join('.');
  }
  return formPath;
}

export function resolvePrecisionForFormPath(
  formPath: string,
  config: FormPrecisionConfig | undefined,
): number | undefined {
  if (!config) return undefined;

  const relative = toDomainRelativeFormPath(formPath);
  const leaf = relative.split('.').pop() ?? relative;
  const entry = config.fields[relative] ?? config.fields[leaf];

  if (entry) return entry.inputPrecision;
  if (config.formDefaults?.inputPrecision != null) return config.formDefaults.inputPrecision;
  return undefined;
}

/**
 * Normalize programmatic numeric writes (effects, observers, lookups) to the
 * target field's resolved inputPrecision contract.
 */
export function normalizeProgrammaticNumericValue(
  value: unknown,
  targetFormPath: string,
  config: FormPrecisionConfig | undefined,
): unknown {
  if (value == null || value === '') return value;
  if (typeof value !== 'number' || Number.isNaN(value)) return value;

  const precision = resolvePrecisionForFormPath(targetFormPath, config);
  if (precision == null) return value;

  return roundToDecimalPlaces(value, precision);
}

export function applyMathPrecision(value: number, precision?: number): number {
  const dp = precision ?? ENGINE_DEFAULTS.INPUT_PRECISION;
  return roundToDecimalPlaces(value, dp);
}
